import { LogLevel } from '@amplitude/analytics-types';
import type { AmplitudeLoadOptions } from '@apollo-frontend/tracking-core';
import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import type { ProviderConfig as LaunchDarklyConfig } from 'launchdarkly-react-client-sdk';
import { parse } from 'query-string';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { redactSensitiveUrl } from 'src/lib/analytics/redactSensitiveUrl';
import {
  LaunchDarklyClientID,
  launchDarklyConfig,
} from 'src/lib/launchDarkly/config';
import type { AmplitudeEnvironment } from 'src/lib/tracking/amplitudeApiKey';

type StudioAmplitudeOptions = Omit<
  AmplitudeLoadOptions,
  'apiKey' | 'product' | 'environment'
> & { environment: AmplitudeEnvironment };

enum StripePublicKeys {
  Test = 'pk_test_51KnlNlF5LMzPO0GwZKsMQvQyRGLP6LS8A9zNUwrcaEw1dZwkCsmU7OwS9IkHPhE8pYLxuknXzPdjYdzjNMmOD9jL00FgRbNzJw',
  Prod = 'pk_live_51KnlNlF5LMzPO0Gwymqy4mejkll1UtXGVIc3cnkZXxp2fuVYekfHRzsaTJFCH3JNnS9DAEliKBd00wONRK0vtIa800AmIbkaeF',
}

/**
 * There are currently 3 ways to set the environment that will be used and the order of precedence
 * 1) __prod__, __dev__, and __local__ query params will override the backend URL and set the client name to custom
 * 2) the URL will be used to determine the environment
 */

export enum StudioConfigEnv {
  Dev = 'dev',
  Staging = 'staging',
  Dev0 = 'dev0',
  Local = 'local',
  Prod = 'prod',
  LocalProd = 'local-prod',
  Test = 'test',
}

interface FrontendConfig<Key extends StudioConfigEnv | 'custom'> {
  env: Key;
  studioUrl: string;
  enablePersistedQueries: boolean;
  amplitudeOptions: StudioAmplitudeOptions;
  shouldAppendOperationNameToGraphqlNetworkRequests: boolean;

  // datadog RUM options
  dataDogRumOptions: RumInitConfiguration;

  // Values linked to the backend
  isProdBackend: boolean;
  engineBackendUrl?: string;
  adminUrl?: string;
  recaptchaSiteKey?: string;
  recurlyPublicKey: string;
  stripePublicKey: StripePublicKeys;
  launchDarkly: LaunchDarklyConfig & { defaultUserID: string };
  apolloClientClientName: string;
}

const parsedQueryParams = parse(window.location.search);

function getEnv() {
  // #1 __prod__, __dev0__, and __local__ can be
  const [override, ...rest] = [
    StudioConfigEnv.Prod,
    StudioConfigEnv.Dev0,
    StudioConfigEnv.Local,
  ].filter((backend) => {
    if (parsedQueryParams[`__${backend}__`]) {
      throw new Error(
        `unknown value for queryParam __${backend}__, to set it, don't provide a value, just \`?__${backend}__\``,
      );
    }
    return parsedQueryParams[`__${backend}__`] === null;
  });
  if (rest.length > 0) {
    throw new Error(
      `Multiple queryparam env overrides present, only 1 supported`,
    );
  }
  if (override) return { env: override, isCustom: true };

  // #2 if no url envs are present, pick env based on the host
  const isProd = [
    'studio.apollographql.com',
    'sandbox.embed.apollographql.com',
    'explorer.embed.apollographql.com',
    'studio-eu.apollographql.com',
    'apollo-studio.netlify.app',
  ].includes(window.location.hostname);
  const isStaging =
    window.location.hostname === 'studio-staging.apollographql.com';
  const isDev0 = window.location.hostname === 'studio-dev0.apollographql.com';
  const isDeployPreview = window.location.hostname.match(
    /^deploy-preview-(\d+)--apollo-studio\.netlify\.app$/,
  );
  const isChromatic = !!window.location.hostname.match(/chromatic.com$/);

  const isLocalProd =
    window.location.hostname === 'prod-local-studio.apollographql.com';
  const isLocalStaging =
    window.location.hostname === 'staging-local-studio.apollographql.com';
  const isLocalDev0 =
    window.location.hostname === 'dev0-local-studio.apollographql.com';

  if (isStaging) {
    return { env: StudioConfigEnv.Staging, isCustom: false };
  } else if (isProd) {
    return { env: StudioConfigEnv.Prod, isCustom: false };
  } else if (isDev0) {
    return { env: StudioConfigEnv.Dev0, isCustom: false };
  } else if (isLocalProd) {
    return { env: StudioConfigEnv.LocalProd, isCustom: true };
  } else if (
    isLocalStaging ||
    isDeployPreview ||
    isChromatic ||
    window.location.hostname === 'local-test.apollographql.com'
  ) {
    return {
      env: StudioConfigEnv.Dev,
      isCustom: true,
      deployPreviewId: isDeployPreview?.[1],
    };
  } else if (isLocalDev0) {
    return { env: StudioConfigEnv.Dev0, isCustom: true };
  } else {
    Sentry.captureException(
      new Error(
        'Unknown host, all hosts should be handled in runtime.ts getEnv',
      ),
      { extra: { host: window.location.hostname } },
    );
    return { env: StudioConfigEnv.Dev, isCustom: true };
  }
}

const disablePersistedQueries = '__nopq__' in parsedQueryParams;

// These are all the options for backends to be used, keys that need to match the backend should go here
const backends = {
  dev0: {
    engineBackendUrl: 'https://graphql-dev0.api.apollographql.com',
    recurlyPublicKey: 'ewr1-8PrdqZT1rSXnSFPyQqrapj',
    stripePublicKey: StripePublicKeys.Test,
    recaptchaSiteKey: '6LenWz0aAAAAAIHllOd43cKTv_inp1zt-NFFg47I',
    launchDarkly: {
      ...launchDarklyConfig,
      clientSideID: LaunchDarklyClientID.Dev0,
      defaultUserID: `anon-studio-${StudioConfigEnv.Dev0}-user`,
    },
  },
  staging: {
    engineBackendUrl: 'https://graphql-staging.api.apollographql.com',
    adminUrl: 'https://admin-staging.apollographql.com',
    recaptchaSiteKey: '6LenWz0aAAAAAIHllOd43cKTv_inp1zt-NFFg47I',
    recurlyPublicKey: 'ewr1-8PrdqZT1rSXnSFPyQqrapj',
    stripePublicKey: StripePublicKeys.Test,
    launchDarkly: {
      ...launchDarklyConfig,
      clientSideID: LaunchDarklyClientID.Staging,
      defaultUserID: `anon-studio-${StudioConfigEnv.Staging}-user`,
    },
  },
  prod: {
    engineBackendUrl: 'https://graphql.api.apollographql.com',
    adminUrl: 'https://admin.apollographql.com',
    recurlyPublicKey: 'ewr1-lcJeRCA9drn8pJQCXHEseT',
    stripePublicKey: StripePublicKeys.Prod,
    recaptchaSiteKey: '6LewylUaAAAAACWN7Ly-d7-0jEuiDkibD8URpbkz',
    launchDarkly: {
      ...launchDarklyConfig,
      clientSideID: LaunchDarklyClientID.Prod,
      defaultUserID: `anon-studio-${StudioConfigEnv.Prod}-user`,
    },
  },
  local: {
    engineBackendUrl: 'http://localhost:4000',
    recurlyPublicKey: 'ewr1-8PrdqZT1rSXnSFPyQqrapj',
    stripePublicKey: StripePublicKeys.Test,
    recaptchaSiteKey: '6LenWz0aAAAAAIHllOd43cKTv_inp1zt-NFFg47I',
    launchDarkly: {
      ...launchDarklyConfig,
      clientSideID: LaunchDarklyClientID.Staging,
      defaultUserID: `anon-studio-${StudioConfigEnv.Local}-user`,
    },
  },
  test: {
    recurlyPublicKey: 'not-a-key',
    stripePublicKey: StripePublicKeys.Test,
    launchDarkly: {
      ...launchDarklyConfig,
      clientSideID: LaunchDarklyClientID.Staging,
      defaultUserID: `anon-studio-${StudioConfigEnv.Test}-user`,
    },
  },
};

/* `beacon` transport is an alternative to xhr and fetch transports in browser.
 * It is designed for tracking pixels, and differs from xhr and fetch in that the request
 * is not cancelled when the current page is unloaded. This allows a tracking 'beacon'
 * to succeed, even if the tracked action is an outbound link.
 * This solution is outlined here: https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-1
 * A browser reference to the underlying feature: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
 */

const getAmplitudeOptions = (
  env: StudioConfigEnv = StudioConfigEnv.Dev,
): StudioAmplitudeOptions => {
  switch (env) {
    case StudioConfigEnv.Staging:
      return {
        environment: 'staging',
        disabled: false,
        client: {
          configuration: {
            cookieOptions: {
              sameSite: 'None',
              secure: true,
            },
            defaultTracking: false,
            logLevel: LogLevel.Warn,
            transport: 'beacon',
          },
        },
      };
    case StudioConfigEnv.Prod:
      return {
        environment: 'prod',
        disabled: false,
        client: {
          configuration: {
            cookieOptions: {
              sameSite: 'None',
              secure: true,
            },
            defaultTracking: false,
            logLevel: LogLevel.Error,
            transport: 'beacon',
          },
        },
      };
    default:
      return {
        environment: 'staging',
        disabled: true,
        client: {
          configuration: {
            cookieOptions: {
              sameSite: 'None',
              secure: true,
            },
            defaultTracking: false,
            logLevel: LogLevel.Verbose,
            transport: 'beacon',
          },
        },
      };
  }
};

const isCypress = 'Cypress' in window;
const isEmbedded = isEmbeddableSandboxRoute() || isEmbeddableExplorerRoute();
const baseDataDogRumOptions: RumInitConfiguration = {
  applicationId: 'd8aa3550-7fea-4ec4-b200-069bede7a568',
  clientToken: 'puba29eeffd4678f57200c16f37c90f5464', // gitleaks:allow
  // `site` refers to the Datadog site parameter of your organization
  // see https://docs.datadoghq.com/getting_started/site/
  site: 'datadoghq.com',
  service: 'studioui',
  // version must match --release-version in the datadog-ci sourcemaps upload CI step
  // so Datadog can correlate minified stack frames back to original source
  version: process.env.GIT_HASH,
  sessionSampleRate: 0,
  sessionReplaySampleRate: 0,
  defaultPrivacyLevel: 'mask-user-input',
  trackUserInteractions: true,
  trackResources: true,
  trackViewsManually: true,
  allowedTracingUrls: [isTracedUrl],
  excludedActivityUrls: [/\/__cypress\//],
  beforeSend: redactSensitiveUrlsFromRumEvent,
};

/**
 * Strips secret tokens out of every RUM event before it leaves the browser.
 *
 * Datadog collects `window.location.href` and `document.referrer` verbatim on
 * views, actions, errors and resources, which would otherwise ship password
 * reset / invitation tokens to `browser-intake-datadoghq.com`. See OE-1393.
 *
 * Returning `true` keeps the (mutated) event; we never drop events here.
 * Must never throw — a throwing `beforeSend` breaks RUM collection entirely.
 *
 * `beforeSend` returns a boolean discard flag rather than a replacement event,
 * so mutating the event in place is the only way the SDK supports editing it —
 * hence the `no-param-reassign` exemption.
 * https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/
 */
/* eslint-disable no-param-reassign */
function redactSensitiveUrlsFromRumEvent(event: RumEvent): boolean {
  try {
    if (event.view) {
      event.view.url = redactSensitiveUrl(event.view.url);
      event.view.referrer = redactSensitiveUrl(event.view.referrer);
    }
    if (event.type === 'resource' && event.resource) {
      event.resource.url = redactSensitiveUrl(event.resource.url);
    }
    if (event.type === 'error' && event.error.resource) {
      event.error.resource.url = redactSensitiveUrl(event.error.resource.url);
    }
  } catch {
    // intentionally swallowed — never break telemetry on a malformed event
  }
  return true;
}
/* eslint-enable no-param-reassign */

const configs: {
  [Key in StudioConfigEnv]: Omit<
    FrontendConfig<Key>,
    'env' | 'isProdBackend' | 'apolloClientClientName'
  >;
} = {
  [StudioConfigEnv.Dev]: {
    ...backends.staging,
    amplitudeOptions: getAmplitudeOptions(),
    studioUrl:
      process.env.LOCAL_EMBED_AUTH_CONTEXT === 'studio-auth' ||
      process.env.LOCAL_EMBED_AUTH_CONTEXT === 'embed'
        ? 'http://localhost:4000'
        : 'https://staging-local-studio.apollographql.com:3000',
    enablePersistedQueries: false,
    shouldAppendOperationNameToGraphqlNetworkRequests: true,
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'dev',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
  [StudioConfigEnv.Staging]: {
    ...backends.staging,
    amplitudeOptions: getAmplitudeOptions(StudioConfigEnv.Staging),
    studioUrl: 'https://studio-staging.apollographql.com',
    enablePersistedQueries: true,
    shouldAppendOperationNameToGraphqlNetworkRequests: true,
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'staging',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
  [StudioConfigEnv.Dev0]: {
    ...backends.dev0,
    amplitudeOptions: getAmplitudeOptions(),
    studioUrl: 'https://studio-dev0.apollographql.com',
    enablePersistedQueries: false,
    shouldAppendOperationNameToGraphqlNetworkRequests: true,
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'dev',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
  [StudioConfigEnv.Local]: {
    ...backends.local,
    studioUrl: 'https://studio.local.apollographql.com',
    enablePersistedQueries: false,
    shouldAppendOperationNameToGraphqlNetworkRequests: true,
    amplitudeOptions: getAmplitudeOptions(),
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'dev',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
  [StudioConfigEnv.Prod]: {
    ...backends.prod,
    studioUrl: 'https://studio.apollographql.com',
    enablePersistedQueries: true,
    shouldAppendOperationNameToGraphqlNetworkRequests: false,
    amplitudeOptions: getAmplitudeOptions(StudioConfigEnv.Prod),
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'prod',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
  [StudioConfigEnv.LocalProd]: {
    ...backends.prod,
    studioUrl: 'https://prod-local-studio.apollographql.com:3000',
    enablePersistedQueries: false,
    shouldAppendOperationNameToGraphqlNetworkRequests: true,
    amplitudeOptions: getAmplitudeOptions(),
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'prod',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
  [StudioConfigEnv.Test]: {
    ...backends.test,
    studioUrl: 'https://staging-local-studio.apollographql.com:3000',
    enablePersistedQueries: true,
    shouldAppendOperationNameToGraphqlNetworkRequests: true,
    amplitudeOptions: getAmplitudeOptions(),
    dataDogRumOptions: {
      ...baseDataDogRumOptions,
      env: 'test',
      sessionSampleRate: 0,
      sessionReplaySampleRate: 0,
    },
  },
};

const { env, isCustom, deployPreviewId } = getEnv();

const customOverrides: Partial<FrontendConfig<'custom'>> = {
  env: 'custom',
  shouldAppendOperationNameToGraphqlNetworkRequests: true,
};

const isProdBackend =
  configs[env].engineBackendUrl === backends.prod.engineBackendUrl;
const isStagingBackend =
  configs[env].engineBackendUrl === backends.staging.engineBackendUrl;

export const runtimeConfig: FrontendConfig<StudioConfigEnv | 'custom'> = {
  env,
  isProdBackend,
  ...configs[env],
  ...(isCustom ? customOverrides : {}),
  // Deploy previews share the Dev config with local hosts, which stay disabled.
  // Previews send to the staging Amplitude project so tracking can be tested before merge.
  ...(deployPreviewId
    ? { amplitudeOptions: getAmplitudeOptions(StudioConfigEnv.Staging) }
    : {}),
  enablePersistedQueries: disablePersistedQueries
    ? false
    : configs[env].enablePersistedQueries,
  apolloClientClientName: `${
    isCypress
      ? `web-e2e-against-${
          isProdBackend ? 'prod' : isStagingBackend ? 'staging' : 'custom'
        }`
      : isEmbedded
        ? `web-${env}-embed`
        : `web-${env}`
  }`,
  dataDogRumOptions: {
    ...configs[env].dataDogRumOptions,
    // Deploy previews get their own env tag so they can be filtered separately
    // in Datadog retention filters (e.g. lower sample rate than prod/staging).
    ...(deployPreviewId ? { env: `deploy-preview-${deployPreviewId}` } : {}),
    // 100% sample rate for all deployed environments; 0% for cypress runs and local dev.
    // Retention filters keep 100% of error sessions and 5% of healthy sessions —
    // see Datadog > Digital Experience > Retention Filters for storage cost controls.
    // Replays are set to 0 here; the "Sessions with errors" retention filter handles
    // replay retention for sessions worth debugging.
    sessionSampleRate: isCypress || env === StudioConfigEnv.Local ? 0 : 100,
    sessionReplaySampleRate: 0,
    // allow automated tests to send events
    allowUntrustedEvents: isCypress,
  },
};

// Hosts whose CORS policy does not allow Datadog's injected request headers
// (e.g. traceparent, x-datadog-origin): the preflight gets no CORS headers, so
// the request is blocked.  studio-ui-deployments is the GCS bucket that
// `useIsAppOffline` polls to detect reconnection.
const UNTRACED_URL_PREFIXES = [
  'https://status.apollographql.com',
  'https://studio-ui-deployments.apollographql.com',
];

/**
 * Returns true for URLs that Datadog RUM should trace.
 */
export function isTracedUrl(url: string): boolean {
  return (
    /^https?:\/\/[^/]*\.apollographql\.com/.test(url) &&
    !UNTRACED_URL_PREFIXES.some((prefix) => url.startsWith(prefix))
  );
}
